import DeliveryTime from '@/components/delivery-time';
import LoaderIcon from '@/components/loader-icon';
import testmonials from '@/data/testmonials';
import { useI18n } from '@/lib/i18n';
import { OrderInteraction, OrderPlatform, PlatformReference, SocialMediaProfile } from '@/types';
import { FireIcon, HandThumbUpIcon, RocketLaunchIcon } from '@heroicons/react/24/outline';
import { ChatBubbleOvalLeftIcon, HeartIcon } from '@heroicons/react/24/solid';
import { useForm, usePage } from '@inertiajs/react';
import axios from 'axios';
import { FormEvent, useMemo, useState } from 'react';

export default function PlatformReferenceInput({
  platform,
  interaction,
  deliveryInMinutes,
  rating,
  onChange,
}: {
  platform: OrderPlatform;
  interaction: OrderInteraction;
  deliveryInMinutes: number;
  rating: number;
  onChange: (data: PlatformReference) => void;
}) {
  const { t, formatNumberShort } = useI18n();
  const { props } = usePage<{ amountOfOrders: number }>();
  const [isLoadingProfile, setIsLoadingProfile] = useState(false);
  const [reference, setReference] = useState<PlatformReference | null>(null);
  const [profile, setProfile] = useState<SocialMediaProfile | null>(null);
  const { data, setData, processing, errors, setError, clearErrors } = useForm({
    platform,
    username: '',
  });

  const amountOfReviews = useMemo(() => Math.round(testmonials.filter((t) => t.interaction === interaction).length * 9.4), []);

  function submit(e: FormEvent<HTMLFormElement>) {
    if (processing) return;

    e.preventDefault();

    loadProfile();
  }

  function loadProfile(nextPage: string | null = null) {
    setIsLoadingProfile(true);

    axios
      .post<SocialMediaProfile>(route('socialMediaPosts'), { ...data, page: nextPage })
      .then(({ data }) => {
        if (interaction === OrderInteraction.Followers) {
          onChange({ url: data.user.url, user: data.user, post: null });
        }

        setProfile((p) => (p ? { ...p, posts: [...p.posts, ...data.posts], next_page: data.next_page } : data));
      })
      .catch(({ status, message, response }) => {
        if (status === 422) {
          setError('username', response.data.message);
        } else {
          setError('username', `${t('components.products_form.Something went wrong')} (${message})`);
        }
      })
      .finally(() => {
        setIsLoadingProfile(false);
      });
  }

  return (
    <form className={'mt-4'} onSubmit={submit}>
      {profile && interaction !== OrderInteraction.Followers ? (
        <div className={'overflow-hidden rounded-xl border border-gray-300'}>
          <div className={'flex items-center bg-gray-50 p-2.5'}>
            <img src={profile.user.avatar} alt={profile.user.username} className={'size-10 rounded-full object-cover'} />
            <div className={'ml-2 flex-1 overflow-hidden'}>
              <div className={'truncate leading-none font-bold'}>{profile.user.name}</div>
              <div className={'text-sm text-gray-700'}>@{profile.user.username}</div>
            </div>
          </div>

          <div className={'grid grid-cols-3 gap-1.5 p-2.5 md:grid-cols-4'}>
            {profile.posts.length === 0 && (
              <div className={'col-span-3 text-center md:col-span-4'}>
                {t('components.products_form.We could not find any posts by :username', { username: profile.user.username })}
              </div>
            )}

            {profile.posts.map((post) => (
              <button
                key={post.id}
                type={'button'}
                id={`post-${post.id}`}
                className={`relative cursor-pointer overflow-hidden rounded-md border-2 ${reference?.url === post.url ? 'border-white shadow-md outline-2 outline-black' : 'border-transparent hover:outline-2 hover:outline-black'} ${reference && reference?.url !== post.url ? 'opacity-50' : ''}`}
                onClick={() => {
                  if (post.url === reference?.url) {
                    setReference(null);
                  } else {
                    setReference({ url: post.url, user: profile!.user, post });
                  }
                }}
              >
                <div className="absolute inset-0 flex items-end space-x-2 bg-gradient-to-t from-black/70 via-black/20 to-transparent p-1 text-white">
                  <div className={'flex items-center rounded-full text-xs font-semibold'}>
                    <HeartIcon className={'mr-0.5 size-3'} />
                    {formatNumberShort(post.likes)}
                  </div>
                  <div className={'flex items-center rounded-full text-xs font-semibold'}>
                    <ChatBubbleOvalLeftIcon className={'mr-0.5 size-3'} />
                    {formatNumberShort(post.comments)}
                  </div>
                </div>

                <img src={post.thumbnail} alt={post.id} className={'aspect-square w-full bg-gray-100 object-cover'} />
              </button>
            ))}
          </div>

          {profile && profile.next_page && (
            <div className="p-2.5 pt-0">
              {isLoadingProfile ? (
                <LoaderIcon />
              ) : (
                <button
                  type={'button'}
                  className={'w-full rounded-xl px-4 py-3 text-center font-bold text-black hover:underline'}
                  disabled={isLoadingProfile}
                  onClick={() => loadProfile(profile!.next_page!)}
                >
                  {t('components.products_form.Load more posts')}
                </button>
              )}
            </div>
          )}

          <div className="p-2.5 pt-0">
            <button
              type={'submit'}
              className={'w-full rounded-xl bg-black px-4 py-3 text-center font-bold text-white not-disabled:hover:opacity-75 disabled:bg-gray-300'}
              disabled={!reference}
              onClick={() => onChange(reference!)}
            >
              {t('components.products_form.Choose post')} &rarr;
            </button>
          </div>
        </div>
      ) : (
        <>
          <div className={'relative'}>
            {isLoadingProfile && (
              <div className="absolute inset-0 z-10 flex flex-col items-center justify-center bg-white/50">
                <LoaderIcon />

                <div className={'mt-1 font-semibold'}>{t('components.products_form.Loading profile')}</div>
              </div>
            )}

            <label htmlFor="username" className={'font-semibold text-gray-700'}>
              {t('components.products_form.Your :Platform username:', { platform })}
            </label>

            <div className="relative flex items-center rounded-xl border border-gray-300">
              <label htmlFor={'username'} className={'p-2.5 pr-0 text-gray-700 md:text-lg'}>
                @
              </label>
              <input
                id="username"
                name="username"
                type="text"
                className={'w-full p-2.5 focus:outline-0 md:text-lg'}
                required={true}
                placeholder={'boostbuddy'}
                value={data.username}
                onChange={(e) => {
                  setData('username', e.target.value);
                  clearErrors('username');
                }}
              />
            </div>
            {errors.username && <div className={'mt-1 rounded bg-red-50 p-2 text-xs text-red-950'}>{errors.username}</div>}
          </div>

          <button
            type={'submit'}
            className={
              'mt-4 w-full rounded-xl bg-black px-4 py-3 text-center font-bold text-white not-disabled:hover:opacity-75 disabled:bg-gray-300'
            }
            disabled={isLoadingProfile}
          >
            {t('components.products_form.Continue')} &rarr;
          </button>

          <div className="mt-4 grid grid-cols-3 gap-1">
            <div className={'flex flex-col items-center justify-center'}>
              <RocketLaunchIcon className={'mb-1 size-6 text-pink-600 md:size-7'} />
              <div className={'text-center text-xs md:text-sm'}>
                {t('components.products_form.Delivers in')}
                <br />
                <span className={'rounded bg-pink-600/10 p-0.5 px-1 font-semibold text-pink-600'}>
                  <DeliveryTime minutes={deliveryInMinutes} />
                </span>
                !
              </div>
            </div>

            <div className={'flex flex-col items-center justify-center'}>
              <FireIcon className={'mb-1 size-6 text-indigo-600 md:size-7'} />
              <div className={'text-center text-xs md:text-sm'}>
                <span className={'rounded bg-indigo-500/10 p-0.5 px-1 font-semibold text-indigo-600'}>
                  {props.amountOfOrders} {t('components.products_form.purchases')}
                </span>
                <br />
                {t('components.products_form.last hour')}!
              </div>
            </div>

            <div className={'flex flex-col items-center justify-center'}>
              <HandThumbUpIcon className={'mb-1 size-6 text-green-600 md:size-7'} />
              <div className={'text-center text-xs md:text-sm'}>
                {t('components.products_form.Rated')}{' '}
                <span className={'rounded bg-green-500/10 p-0.5 px-1 font-semibold text-green-600'}>
                  {rating}
                  <span className="ml-0.5">&#9733;</span>
                </span>
                <br />
                {t('components.products_form.from :amountOfReviews buyers!', { amountOfReviews })}
              </div>
            </div>
          </div>
        </>
      )}
    </form>
  );
}
